import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Download, Loader2 } from 'lucide-react';

// Field tier definitions (matching backend)
const TIER_1_ESSENTIAL_FIELDS = [
  'first_name', 'last_name', 'email', 'phone',
  'business_name', 'location', 'industry', 'annual_revenue',
  'services', 'timeline', 'story', 'status', 'created_at',
  'email_verified'
];

const TIER_2_QUALIFIED_FIELDS = [
  'audience', 'entity_type', 'accounting_software',
  'states', 'revenue_location', 'referral_source',
  'referrer_name', 'consent', 'values_fit', 'services_interested'
];

const TIER_3_SCORING_FIELDS = [
  'grade', 'score', 'breakdown', 'routing_info',
  'assigned_to', 'assigned_by', 'assigned_at'
];

const TIER_4_SECURITY_FIELDS = [
  'ip_address', 'country', 'city', 'region',
  'latitude', 'longitude', 'postal_code', 'timezone', 'isp',
  'vpn', 'tor', 'proxy', 'hosting', 'high_risk_ip',
  'spam_score', 'spam_flags'
];

const TIER_5_SYSTEM_FIELDS = [
  '_id', 'updated_at', 'updated_by', 'verified_at'
];

// Export presets
const EXPORT_PRESETS = {
  basic_contact: {
    name: 'Basic Contact Info',
    description: 'Essential contact details only',
    fields: ['first_name', 'last_name', 'email', 'phone', 'business_name']
  },
  sales_team: {
    name: 'Sales Team View',
    description: 'Contact details + business context',
    fields: [
      ...['first_name', 'last_name', 'email', 'phone', 'business_name'],
      'location', 'industry', 'annual_revenue', 'services', 'timeline', 'status'
    ]
  },
  account_manager: {
    name: 'Account Manager View',
    description: 'Full qualified lead details',
    fields: [
      ...TIER_1_ESSENTIAL_FIELDS,
      ...TIER_2_QUALIFIED_FIELDS
    ]
  },
  admin_full: {
    name: 'Admin Full Export',
    description: 'All fields including scoring and security',
    fields: [
      ...TIER_1_ESSENTIAL_FIELDS,
      ...TIER_2_QUALIFIED_FIELDS,
      ...TIER_3_SCORING_FIELDS,
      ...TIER_4_SECURITY_FIELDS,
      ...TIER_5_SYSTEM_FIELDS
    ]
  }
};

// Field display names
const FIELD_LABELS = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone: 'Phone',
  business_name: 'Business Name',
  location: 'Location',
  industry: 'Industry',
  annual_revenue: 'Annual Revenue',
  services: 'Services',
  timeline: 'Timeline',
  story: 'Story',
  status: 'Status',
  created_at: 'Created Date',
  email_verified: 'Email Verified',
  audience: 'Audience',
  entity_type: 'Entity Type',
  accounting_software: 'Accounting Software',
  states: 'States',
  revenue_location: 'Revenue Location',
  referral_source: 'Referral Source',
  referrer_name: 'Referrer Name',
  consent: 'Consent',
  values_fit: 'Values Fit',
  services_interested: 'Services Interested',
  grade: 'Grade',
  score: 'Score',
  breakdown: 'Score Breakdown',
  routing_info: 'Routing Info',
  assigned_to: 'Assigned To',
  assigned_by: 'Assigned By',
  assigned_at: 'Assigned Date',
  ip_address: 'IP Address',
  country: 'Country',
  city: 'City',
  region: 'Region',
  latitude: 'Latitude',
  longitude: 'Longitude',
  postal_code: 'Postal Code',
  timezone: 'Timezone',
  isp: 'ISP',
  vpn: 'VPN',
  tor: 'TOR',
  proxy: 'Proxy',
  hosting: 'Hosting',
  high_risk_ip: 'High Risk IP',
  spam_score: 'Spam Score',
  spam_flags: 'Spam Flags',
  _id: 'ID',
  updated_at: 'Updated Date',
  updated_by: 'Updated By',
  verified_at: 'Verified Date'
};

export default function ExportModal({ 
  open, 
  onClose, 
  onExport, 
  isLoading,
  currentUser 
}) {
  const [selectedPreset, setSelectedPreset] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setSelectedPreset('');
      setSelectedFields([...TIER_1_ESSENTIAL_FIELDS.slice(0, 5)]); // Default to basic contact
    }
  }, [open]);

  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    if (presetKey && EXPORT_PRESETS[presetKey]) {
      setSelectedFields([...EXPORT_PRESETS[presetKey].fields]);
    }
  };

  const toggleField = (field) => {
    setSelectedFields(prev => 
      prev.includes(field)
        ? (prev || []).filter(f => f !== field)
        : [...prev, field]
    );
    setSelectedPreset(''); // Clear preset when manually selecting
  };

  const toggleTier = (tierFields) => {
    const allSelected = (tierFields || []).every(f => selectedFields.includes(f));
    if (allSelected) {
      // Deselect all
      setSelectedFields(prev => (prev || []).filter(f => !tierFields.includes(f)));
    } else {
      // Select all
      setSelectedFields(prev => [...new Set([...prev, ...tierFields])]);
    }
    setSelectedPreset(''); // Clear preset
  };

  const handleExport = () => {
    if (selectedFields.length === 0) {
      return;
    }
    onExport(selectedFields);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-ivory">
        <DialogHeader>
          <DialogTitle className="text-navy">Export Leads to CSV</DialogTitle>
          <DialogDescription>
            Select which fields to include in your export. You can choose a preset or customize your selection.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Preset Selection */}
          <div>
            <label className="text-sm font-medium text-navy mb-2 block">Choose a Preset</label>
            <Select data-testid="export-modal-select-1" value={selectedPreset} onValueChange={handlePresetChange}>
              <SelectTrigger className="bg-white border-gray-300" data-testid="export-preset-select">
                <SelectValue placeholder="Select a preset or customize below" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EXPORT_PRESETS).map(([key, preset]) => (
                  <SelectItem data-testid="export-modal-selectitem-1" key={key} value={key}>
                    <div>
                      <div className="font-medium">{preset.name}</div>
                      <div className="text-xs text-gray-500">{preset.description} ({preset.fields.length} fields)</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Field Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-navy">Or Customize Fields</label>
              <span className="text-xs text-gray-600">
                {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            
            <ScrollArea className="h-96 border border-gray-300 rounded-lg bg-white p-4">
              <Accordion type="multiple" className="w-full">
                {/* Tier 1: Essential Fields */}
                <AccordionItem value="tier1">
                  <AccordionTrigger data-testid="export-modal-accordiontrigger-5" className="text-sm font-medium">
                    <div className="flex justify-between w-full pr-4">
                      <span>Essential Fields (Tier 1)</span>
                      <span className="text-xs text-gray-500">
                        {TIER_1_ESSENTIAL_FIELDS.filter(f => selectedFields.includes(f)).length}/{TIER_1_ESSENTIAL_FIELDS.length}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-2 pt-2">
                    <div className="flex items-center space-x-2 mb-3">
                      <Button data-testid="export-modal-button-5"
                        variant="outline"
                        size="sm"
                        onClick={() => toggleTier(TIER_1_ESSENTIAL_FIELDS)}
                        className="text-xs"
                      >
                        {TIER_1_ESSENTIAL_FIELDS.every(f => selectedFields.includes(f)) ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    {TIER_1_ESSENTIAL_FIELDS.map(field => (
                      <div key={field} className="flex items-center space-x-2">
                        <Checkbox
                          id={`field-${field}`}
                          checked={selectedFields.includes(field)}
                          onCheckedChange={() => toggleField(field)}
                          data-testid={`export-field-${field}`}
                        />
                        <label 
                          htmlFor={`field-${field}`} 
                          className="text-sm cursor-pointer flex-1"
                        >
                          {FIELD_LABELS[field] || field}
                        </label>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>

                {/* Tier 2: Qualified Fields */}
                <AccordionItem value="tier2">
                  <AccordionTrigger data-testid="export-modal-accordiontrigger-4" className="text-sm font-medium">
                    <div className="flex justify-between w-full pr-4">
                      <span>Qualified Lead Details (Tier 2)</span>
                      <span className="text-xs text-gray-500">
                        {TIER_2_QUALIFIED_FIELDS.filter(f => selectedFields.includes(f)).length}/{TIER_2_QUALIFIED_FIELDS.length}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-2 pt-2">
                    <div className="flex items-center space-x-2 mb-3">
                      <Button data-testid="export-modal-button-4"
                        variant="outline"
                        size="sm"
                        onClick={() => toggleTier(TIER_2_QUALIFIED_FIELDS)}
                        className="text-xs"
                      >
                        {TIER_2_QUALIFIED_FIELDS.every(f => selectedFields.includes(f)) ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    {TIER_2_QUALIFIED_FIELDS.map(field => (
                      <div key={field} className="flex items-center space-x-2">
                        <Checkbox
                          id={`field-${field}`}
                          checked={selectedFields.includes(field)}
                          onCheckedChange={() => toggleField(field)}
                          data-testid={`export-field-${field}`}
                        />
                        <label 
                          htmlFor={`field-${field}`} 
                          className="text-sm cursor-pointer flex-1"
                        >
                          {FIELD_LABELS[field] || field}
                        </label>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>

                {/* Tier 3: Scoring (Admin Only) */}
                {currentUser?.role === 'admin' && (
                  <AccordionItem value="tier3">
                    <AccordionTrigger data-testid="export-modal-accordiontrigger-3" className="text-sm font-medium">
                      <div className="flex justify-between w-full pr-4">
                        <span>Scoring & Routing (Tier 3 - Admin Only)</span>
                        <span className="text-xs text-gray-500">
                          {TIER_3_SCORING_FIELDS.filter(f => selectedFields.includes(f)).length}/{TIER_3_SCORING_FIELDS.length}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2 pt-2">
                      <div className="flex items-center space-x-2 mb-3">
                        <Button data-testid="export-modal-button-3"
                          variant="outline"
                          size="sm"
                          onClick={() => toggleTier(TIER_3_SCORING_FIELDS)}
                          className="text-xs"
                        >
                          {TIER_3_SCORING_FIELDS.every(f => selectedFields.includes(f)) ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      {TIER_3_SCORING_FIELDS.map(field => (
                        <div key={field} className="flex items-center space-x-2">
                          <Checkbox
                            id={`field-${field}`}
                            checked={selectedFields.includes(field)}
                            onCheckedChange={() => toggleField(field)}
                            data-testid={`export-field-${field}`}
                          />
                          <label 
                            htmlFor={`field-${field}`} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {FIELD_LABELS[field] || field}
                          </label>
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Tier 4: Security (Admin Only) */}
                {currentUser?.role === 'admin' && (
                  <AccordionItem value="tier4">
                    <AccordionTrigger data-testid="export-modal-accordiontrigger-2" className="text-sm font-medium">
                      <div className="flex justify-between w-full pr-4">
                        <span>Security & Fraud Detection (Tier 4 - Admin Only)</span>
                        <span className="text-xs text-gray-500">
                          {TIER_4_SECURITY_FIELDS.filter(f => selectedFields.includes(f)).length}/{TIER_4_SECURITY_FIELDS.length}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2 pt-2">
                      <div className="flex items-center space-x-2 mb-3">
                        <Button data-testid="export-modal-button-2"
                          variant="outline"
                          size="sm"
                          onClick={() => toggleTier(TIER_4_SECURITY_FIELDS)}
                          className="text-xs"
                        >
                          {TIER_4_SECURITY_FIELDS.every(f => selectedFields.includes(f)) ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      {TIER_4_SECURITY_FIELDS.map(field => (
                        <div key={field} className="flex items-center space-x-2">
                          <Checkbox
                            id={`field-${field}`}
                            checked={selectedFields.includes(field)}
                            onCheckedChange={() => toggleField(field)}
                            data-testid={`export-field-${field}`}
                          />
                          <label 
                            htmlFor={`field-${field}`} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {FIELD_LABELS[field] || field}
                          </label>
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Tier 5: System (Admin Only) */}
                {currentUser?.role === 'admin' && (
                  <AccordionItem value="tier5">
                    <AccordionTrigger data-testid="export-modal-accordiontrigger-1" className="text-sm font-medium">
                      <div className="flex justify-between w-full pr-4">
                        <span>System & Audit Fields (Tier 5 - Admin Only)</span>
                        <span className="text-xs text-gray-500">
                          {TIER_5_SYSTEM_FIELDS.filter(f => selectedFields.includes(f)).length}/{TIER_5_SYSTEM_FIELDS.length}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2 pt-2">
                      <div className="flex items-center space-x-2 mb-3">
                        <Button data-testid="export-modal-button-1"
                          variant="outline"
                          size="sm"
                          onClick={() => toggleTier(TIER_5_SYSTEM_FIELDS)}
                          className="text-xs"
                        >
                          {TIER_5_SYSTEM_FIELDS.every(f => selectedFields.includes(f)) ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      {TIER_5_SYSTEM_FIELDS.map(field => (
                        <div key={field} className="flex items-center space-x-2">
                          <Checkbox
                            id={`field-${field}`}
                            checked={selectedFields.includes(field)}
                            onCheckedChange={() => toggleField(field)}
                            data-testid={`export-field-${field}`}
                          />
                          <label 
                            htmlFor={`field-${field}`} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {FIELD_LABELS[field] || field}
                          </label>
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isLoading}
            data-testid="export-cancel-button"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExport}
            disabled={isLoading || selectedFields.length === 0}
            className="bg-navy hover:bg-navy/90"
            data-testid="export-confirm-button"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export ({selectedFields.length} fields)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
